import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { emailScheduleService } from '../../../services/emailScheduleService';

const RecipientManager = ({ schedule, onClose }) => {
  const [recipients, setRecipients] = useState(schedule?.recipients || []);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ recipientName: '', recipientEmail: '' });
  const [loading, setLoading] = useState(false);

  const handleAddRecipient = async (e) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      const newRecipient = await emailScheduleService?.addRecipient(schedule?.id, {
        recipientName: formData?.recipientName,
        recipientEmail: formData?.recipientEmail,
        isActive: true
      });
      
      setRecipients([...recipients, newRecipient]);
      setFormData({ recipientName: '', recipientEmail: '' });
      setShowForm(false);
    } catch (error) {
      console.error('Error adding recipient:', error);
      alert('Failed to add recipient');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (recipient) => {
    try {
      const updated = await emailScheduleService?.updateRecipient(recipient?.id, {
        isActive: !recipient?.isActive
      });
      
      setRecipients(recipients?.map(r => r?.id === recipient?.id ? updated : r));
    } catch (error) {
      console.error('Error updating recipient:', error);
      alert('Failed to update recipient');
    }
  };

  const handleDeleteRecipient = async (recipientId) => {
    if (!window.confirm('Are you sure you want to remove this recipient?')) return;
    
    try {
      await emailScheduleService?.deleteRecipient(recipientId);
      setRecipients(recipients?.filter(r => r?.id !== recipientId));
    } catch (error) {
      console.error('Error deleting recipient:', error);
      alert('Failed to delete recipient');
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-heading font-bold text-foreground">
              Manage Recipients
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {schedule?.reportName}
            </p>
          </div>
          <button
            onClick={() => onClose(true)}
            className="p-2 hover:bg-muted rounded-lg transition-smooth press-effect focus-ring"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="w-full px-4 py-3 border-2 border-dashed border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-smooth text-muted-foreground hover:text-primary flex items-center justify-center gap-2"
            >
              <Icon name="Plus" size={20} />
              <span>Add Recipient</span>
            </button>
          ) : (
            <form onSubmit={handleAddRecipient} className="bg-muted/50 p-4 rounded-lg space-y-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={formData?.recipientName}
                  onChange={(e) => setFormData({ ...formData, recipientName: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  placeholder="Recipient name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData?.recipientEmail}
                  onChange={(e) => setFormData({ ...formData, recipientEmail: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  placeholder="recipient@example.com"
                  required
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setFormData({ recipientName: '', recipientEmail: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth press-effect focus-ring"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Adding...' : 'Add Recipient'}
                </button>
              </div>
            </form>
          )}

          <div className="space-y-3 mt-4">
            {recipients?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Icon name="Users" size={48} className="mx-auto mb-2 opacity-50" />
                <p>No recipients added yet</p>
              </div>
            ) : (
              recipients?.map((recipient) => (
                <div
                  key={recipient?.id}
                  className="flex items-center justify-between p-4 bg-background border border-border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      recipient?.isActive ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      <Icon name="User" size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{recipient?.recipientName}</p>
                      <p className="text-sm text-muted-foreground">{recipient?.recipientEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(recipient)}
                      className={`px-3 py-1 rounded-lg text-sm transition-smooth press-effect focus-ring ${
                        recipient?.isActive
                          ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {recipient?.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => handleDeleteRecipient(recipient?.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-smooth press-effect focus-ring"
                    >
                      <Icon name="Trash2" size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipientManager;